"use client";

import React from "react";
import { ArrowRight, Crosshair, Settings2 } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const fleetData = [{
  id: "cat-336",
  name: "KOBELCO 210 EXCAVATOR",
  role: "Mass Excavation & Deep Foundations",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575346/img/kobelco_210_excavator_digging.png",
  description: "The heavy hitter. It moves mountains and makes quick work of trees, basements and large cut/fill projects.\xA0",
  specs: [{
    label: "Operating Weight",
    value: "81,900 lbs"
  }, {
    label: "Net Power",
    value: "313 HP"
  }, {
    label: "Max Dig Depth",
    value: "26' 10\""
  }, {
    label: "Drawbar Pull",
    value: "66,318 lbf"
  }]
}, {
  id: "komatsu-d61",
  name: "CAT D6 DOZER",
  role: "MASS EARTHWORKS & ROAD BUILDING",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575346/img/komatsu_d61_dozer_fleet.png",
  description: "The D6 and its attached ripper rolls and pushes material by the foot. We use it to accelerate site development.",
  specs: [{
    label: "Operating Weight",
    value: "42,840 lbs"
  }, {
    label: "Net Power",
    value: "168 HP"
  }, {
    label: "Blade Capacity",
    value: "4.5 yd³"
  }, {
    label: "Track Gauge",
    value: "7' 0\""
  }]
}, {
  id: "volvo-a30g",
  name: "WESTERN STAR QUAD AXLE DUMP TRUCKS",
  role: "Bulk Material Transport",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575346/img/volvo_a30g_hauler_fleet.png",
  description: "A fleet of quads and million mile drivers that are on call to deliver at a moments notice. We delivers tons with the efficiency of a Swiss watch and none of the 'I can get there next week' drama.",
  specs: [{
    label: "Payload Capacity",
    value: "31.9 tons"
  }, {
    label: "Gross Power",
    value: "355 HP"
  }, {
    label: "Body Volume",
    value: "23.3 yd³"
  }, {
    label: "Max Speed",
    value: "33 mph"
  }],
  imageClassName: "absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent opacity-80 !bg-[url(https://joes-excavating.com/wp-content/uploads/2026/03/Image-4-scaled.png)] absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent opacity-80 !bg-[url(https://joes-excavating.com/wp-content/uploads/2026/03/Image-4-scaled.png)] absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent opacity-80 !bg-[url(https://joes-excavating.com/wp-content/uploads/2026/03/Image-4-scaled.png)] absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent opacity-80 !bg-[url(https://joes-excavating.com/wp-content/uploads/2026/03/Image-4-scaled.png)] absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent opacity-80 !bg-[url(https://joes-excavating.com/wp-content/uploads/2026/03/Image-4-scaled.png)]"
}, {
  id: "cat-308",
  name: "BOBCAT MINI EXCAVATOR",
  role: "CONFINED SPACE & ROCK WALLS",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575346/img/cat_308_mini_excavator_fleet.png",
  description: "Proof that size isn't everything. It fits in tight spots and works with extreme efficiency, leaving your landscaping intact and your job done with surgical precision.",
  specs: [{
    label: "Operating Weight",
    value: "20,640 lbs"
  }, {
    label: "Net Power",
    value: "69.5 HP"
  }, {
    label: "Max Dig Depth",
    value: "15' 3\""
  }, {
    label: "Tail Swing",
    value: "Compact"
  }]
}];
export default function TheHeavyIronFleet() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-heavy-iron-fleet" className="dark bg-background text-foreground py-24 lg:py-32 overflow-hidden border-t border-border/50 relative">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#2F2F2F_1px,transparent_1px),linear-gradient(to_bottom,#2F2F2F_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Settings2 className="w-5 h-5 text-primary" />
              <span className="text-primary font-mono text-sm font-bold tracking-widest uppercase">How we get the job done</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white mb-6 leading-[0.9]">
              The Heavy <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/70">
                Iron Fleet
              </span>
            </h2>
            <p className="text-muted-foreground text-lg md:text-xl max-w-xl leading-relaxed">Our fleet consists of shiny, GPS-guided, high-performance monsters that work faster, smarter, safer and definitely cleaner to elevate your experience and get the job done right.</p>
          </div>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full">
          {}
          <div className="hidden md:flex justify-end gap-2 mb-6 absolute top-0 right-4 md:right-6 z-20">
            <CarouselPrevious className="static translate-y-0 translate-x-0 h-14 w-14 rounded-none border-2 border-secondary bg-background hover:border-primary hover:bg-secondary hover:text-primary transition-all duration-300 [&_svg]:size-6" />
            <CarouselNext className="static translate-y-0 translate-x-0 h-14 w-14 rounded-none border-2 border-secondary bg-background hover:border-primary hover:bg-secondary hover:text-primary transition-all duration-300 [&_svg]:size-6" />
          </div>

          <CarouselContent className="-ml-4 md:-ml-6">
            {fleetData.map((machine, index) => <CarouselItem key={machine.id} data-index={index} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-[45%] xl:basis-[40%]">
                <div className="group relative flex flex-col h-full bg-card border border-border/50 overflow-hidden transition-colors duration-500 hover:border-primary/50">
                  {}
                  <div className="relative aspect-[4/3] overflow-hidden bg-secondary/30 p-6 flex items-center justify-center">
                    {}
                    <Crosshair className="absolute top-4 left-4 w-4 h-4 text-muted-foreground/30" />
                    <Crosshair className="absolute top-4 right-4 w-4 h-4 text-muted-foreground/30" />
                    <Crosshair className="absolute bottom-4 left-4 w-4 h-4 text-muted-foreground/30" />
                    <Crosshair className="absolute bottom-4 right-4 w-4 h-4 text-muted-foreground/30" />
                    
                    <img src={machine.image} alt={machine.name} className="w-full h-full object-contain filter grayscale-[0.85] contrast-125 brightness-90 group-hover:grayscale-0 group-hover:contrast-100 group-hover:brightness-100 transition-all duration-700 scale-95 group-hover:scale-100" />
                    
                    {}
                    <div className={`${machine.imageClassName || ""} absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent opacity-80`} />
                  </div>

                  {}
                  <div className="flex flex-col flex-grow p-6 md:p-8 relative z-10 -mt-12">
                    <div className="bg-background border border-border/50 p-4 mb-6 shadow-lg inline-block self-start">
                      <span className="text-primary font-mono text-xs font-bold tracking-widest uppercase block mb-1">
                        {machine.role}
                      </span>
                      <h3 className="text-2xl font-black uppercase tracking-tight text-white">
                        {machine.name}
                      </h3>
                    </div>

                    <p className="text-muted-foreground text-sm mb-8 flex-grow">
                      {machine.description}
                    </p>

                    {}
                    <div className="mt-auto">
                      <div className="flex items-center gap-2 mb-3 border-b border-border/50 pb-2">
                        <Settings2 className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          Technical Specifications
                        </span>
                      </div>
                      <Table className="w-full">
                        <TableBody>
                          {machine.specs.map((spec, i) => <TableRow key={i} data-index={i} className="border-border/30 hover:bg-secondary/20 transition-colors">
                              <TableCell className="py-3 px-0 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                {spec.label}
                              </TableCell>
                              <TableCell className="py-3 px-0 text-right font-mono text-sm text-primary font-semibold">
                                {spec.value}
                              </TableCell>
                            </TableRow>)}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  
                  {}
                  <div className="absolute bottom-0 left-0 h-1 w-0 bg-primary transition-all duration-500 group-hover:w-full" />
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          <div className="flex md:hidden justify-center gap-4 mt-8">
            <CarouselPrevious className="static translate-y-0 translate-x-0 h-12 w-12 rounded-none border-2 border-secondary bg-background hover:border-primary hover:bg-secondary hover:text-primary" />
            <CarouselNext className="static translate-y-0 translate-x-0 h-12 w-12 rounded-none border-2 border-secondary bg-background hover:border-primary hover:bg-secondary hover:text-primary" />
          </div>
        </Carousel>
      </div>
    </section>;
}